import React, { FC } from 'react';
import { useTranslation } from 'next-i18next';
import { match } from 'ts-pattern';

import { AuctionCard, TimerOld as Timer } from '@catawiki/cata-style';

import { Auctioneer } from '@/shared/models/auction';
import { formatDayMonth } from '@/shared/formatter';
import { MockAuction } from '@/shared/stubs/auction';

import { ScrollableList } from '../ScrollableList';
import styles from './Row.module.scss';

export interface AuctionInput {
  auctionId: number;
}

export interface Auction {
  id: number;
  href: string;
  title: string;
  curator: Auctioneer;
  mainImage: string;
  thumbImages: [string, string, string];
  numberOfLots: number;
  closeAt: number;
}

interface Props {
  auctionIds?: AuctionInput[];
  auctions?: Auction[];
  locale: string;
}

export const Row: FC<Props> = ({ locale, auctionIds = [], auctions = [] }) => {
  const { t, i18n } = useTranslation();

  const items = match({
    hasIds: auctionIds.length > 0,
    hasItems: auctions.length > 0,
  })
    .with({ hasIds: true, hasItems: false }, () =>
      auctionIds.map((item) => MockAuction(locale, item.auctionId))
    )
    .otherwise(() => auctions);

  return (
    <ScrollableList
      data-testid="row"
      itemClassName={styles.item}
      arrowClassName={styles.arrow}
    >
      {items.map((auction) => {
        const timer = (
          <Timer
            timer={t('closes_on', {
              closed_on_date: formatDayMonth(
                i18n.language,
                new Date(auction.closeAt)
              ),
            })}
          />
        );
        const subTitle = `${t('auction_lane.curated_by')} ${
          auction.curator.name
        }`;
        return (
          <AuctionCard
            key={auction.id}
            subTitle={subTitle}
            {...auction}
            timer={timer}
          />
        );
      })}
    </ScrollableList>
  );
};
